/**
 * Runtime wrapper around the Pagefind search client.
 *
 * Pagefind ships an ESM module at /pagefind/pagefind.js after `npm run build`.
 * We dynamic-import it on first use so the cost is only paid by users who
 * actually search. In dev (where the index doesn't exist) the import fails
 * gracefully and search components show a "not available" state.
 */

export interface PagefindSubResult {
  title: string;
  url: string;
  excerpt: string;
  anchor?: { id?: string };
}

export interface PagefindResultData {
  url: string;
  excerpt: string;
  meta: { title?: string; [k: string]: string | undefined };
  filters?: Record<string, string[]>;
  sub_results?: PagefindSubResult[];
}

export interface PagefindResult {
  id: string;
  data(): Promise<PagefindResultData>;
}

interface PagefindAPI {
  init(): Promise<void>;
  search(query: string, options?: { filters?: Record<string, unknown> }): Promise<{
    results: PagefindResult[];
  }>;
  options?(opts: Record<string, unknown>): Promise<void>;
}

let cached: PagefindAPI | null | undefined;

export async function loadPagefind(): Promise<PagefindAPI | null> {
  if (cached !== undefined) return cached;
  try {
    const url = `${import.meta.env.BASE_URL}pagefind/pagefind.js`;
    const mod = (await import(/* @vite-ignore */ url)) as unknown as PagefindAPI;
    if (typeof mod.init === 'function') await mod.init();
    cached = mod;
    return mod;
  } catch (e) {
    if (typeof console !== 'undefined') {
      console.warn('[search] Pagefind index not available — run `npm run build` to generate it.', e);
    }
    cached = null;
    return null;
  }
}

/**
 * Returns true iff the Pagefind index is reachable from this origin.
 * Use this from UI to distinguish "loaded but no matches" from "never loaded".
 */
export async function isPagefindAvailable(): Promise<boolean> {
  return (await loadPagefind()) !== null;
}

/**
 * Convenience: search with a language filter.
 * Returns up to `limit` results' data (resolved from each result's lazy data()).
 */
export async function searchDocs(
  query: string,
  lang: string,
  limit = 8,
): Promise<PagefindResultData[]> {
  const api = await loadPagefind();
  if (!api) return [];
  const q = query.trim();
  if (!q) return [];
  const { results } = await api.search(q, { filters: { lang } });
  const top = results.slice(0, limit);
  return Promise.all(top.map((r) => r.data()));
}
